/**
 * Quels modules ouvre chaque plan — règle unique, partagée par le serveur
 * (proxy), l'app (useAccessibleModules) et l'écran Admin.
 *
 * Deux familles de plans :
 *  - plans d'abonnement, posés par le webhook Stripe : `pro` (les modules
 *    annoncés sur la vitrine) et `light`. Une personne qui paie reçoit
 *    exactement ce qu'on lui a vendu, rien de plus.
 *  - plans gérés à la main dans Admin : `pro_gestic` (tout, y compris les
 *    modules pas encore vendus comme Santé ou Music), `budget`, `music`.
 *
 * Un plan inconnu retombe sur « tout » : c'est le comportement historique
 * des comptes créés avant l'existence des plans.
 */

export const TOUS_LES_MODULES = ["budget", "music", "famille", "rando", "light", "voiture", "velo", "dispos", "sante"] as const;
export type ModuleApp = (typeof TOUS_LES_MODULES)[number];

/** Plans posés par le webhook Stripe : l'abonnement les tient à jour. */
export const PLANS_ABONNEMENT = ["pro", "light"] as const;

export function estPlanAbonnement(plan: string | null | undefined): boolean {
  return (PLANS_ABONNEMENT as readonly string[]).includes(plan ?? "");
}

/** Gestic Pro : ce que promet la vitrine (le budget porte patrimoine et intermittence). */
export const MODULES_PRO: ModuleApp[] = ["budget", "light", "voiture", "famille"];

export function modulesDuPlan(plan: string | null | undefined): Set<ModuleApp> {
  switch (plan) {
    case "pro":
      return new Set(MODULES_PRO);
    case "light":
    case "budget_light":
      return new Set<ModuleApp>(["light"]);
    case "budget":
      return new Set<ModuleApp>(["budget", "light"]);
    case "music":
      return new Set<ModuleApp>(["music"]);
    default:
      return new Set(TOUS_LES_MODULES); // pro_gestic / historique
  }
}

/**
 * Modules accessibles : des accès donnés à la main (`modules` non nul)
 * priment — même une liste vide, on ne retombe jamais en silence sur
 * « tout » ; sinon le plan décide.
 */
export function modulesAccessibles(plan: string | null | undefined, modules: readonly string[] | null | undefined): Set<ModuleApp> {
  if (modules != null) {
    return new Set(modules.filter((m): m is ModuleApp => (TOUS_LES_MODULES as readonly string[]).includes(m)));
  }
  return modulesDuPlan(plan);
}
